// Live Preview & Play Mode Type Definitions

export interface DevicePreset {
  id: string;
  name: string;
  category: DeviceCategory;
  width: number;
  height: number;
  pixelRatio: number;
  isPortrait: boolean;
  icon?: string;
}

export type DeviceCategory = 'mobile' | 'tablet' | 'desktop' | 'console' | 'custom';

export interface ViewportSettings {
  devicePresetId: string | null;
  customWidth: number;
  customHeight: number;
  isPortrait: boolean;
  scale: number; // 0.25 - 2.0
  fitToPanel: boolean;
  aspectRatioLocked: boolean;
  showLetterbox: boolean;
}

export interface PlayModeSettings {
  onPlayBehavior: 'keep-layout' | 'maximize' | 'focus';
  reloadScripts: boolean;
  resetSceneState: boolean;
  pauseOnStart: boolean;
  stepFrameOnPause: boolean;
  maxFps: number;
  vsyncEnabled: boolean;
}

export interface DebugOverlaySettings {
  enabled: boolean;
  showFps: boolean;
  showFrameTime: boolean;
  showDrawCalls: boolean;
  showObjectCount: boolean;
  showMemory: boolean;
  showTextures: boolean;
  showAudio: boolean;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  opacity: number;
}

export interface DebugRenderSettings {
  showColliders: boolean;
  showGrid: boolean;
  showBounds: boolean;
  showOrigins: boolean;
  lowLatencyMode: boolean;
}

export interface PerformanceStats {
  fps: number;
  frameTime: number;
  minFrameTime: number;
  maxFrameTime: number;
  avgFrameTime: number;
  drawCalls: number;
  triangles: number;
  activeObjects: number;
  visibleObjects: number;
  memoryUsed: number;
  memoryTotal: number;
  texturesLoaded: number;
  textureMemory: number;
  audioPlaying: number;
  audioLoaded: number;
}

export interface InputState {
  keys: Set<string>;
  mousePosition: { x: number; y: number };
  mouseButtons: Set<number>;
  touches: Touch[];
  gamepadConnected: boolean;
  gamepadState: GamepadState | null;
}

export interface Touch {
  id: number;
  x: number;
  y: number;
  force: number;
}

export interface GamepadState {
  axes: number[];
  buttons: boolean[];
}

export interface ScreenshotSettings {
  format: 'png' | 'jpeg' | 'webp';
  quality: number; // 0-1 for jpeg/webp
  includeUI: boolean;
  scale: number;
  filename: string;
}

export interface RecordingSettings {
  format: 'mp4' | 'webm' | 'gif';
  quality: 'low' | 'medium' | 'high';
  fps: number;
  maxDuration: number; // seconds
  includeAudio: boolean;
  filename: string;
}

export interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  frames: number;
  size: number;
}

export type PlayState = 'stopped' | 'playing' | 'paused';

export type PreviewMode = 'canvas' | 'iframe';

export interface SceneSnapshot {
  id: string;
  timestamp: number;
  gameObjects: Record<string, unknown>;
  rootIds: string[];
}

export interface LivePreviewState {
  // Viewport
  viewport: ViewportSettings;

  // Preview mode
  previewMode: PreviewMode;

  // Play mode
  playState: PlayState;
  playModeSettings: PlayModeSettings;
  sceneSnapshot: SceneSnapshot | null;

  // Debug
  debugOverlay: DebugOverlaySettings;
  debugRender: DebugRenderSettings;
  stats: PerformanceStats;

  // Input
  inputState: InputState;
  inputSimulationEnabled: boolean;
  touchSimulationEnabled: boolean;

  // Recording
  screenshotSettings: ScreenshotSettings;
  recordingSettings: RecordingSettings;
  recordingState: RecordingState;

  // Frame control
  currentFrame: number;
  targetFps: number;
  actualFps: number;
  deltaTime: number;
}

export interface LivePreviewActions {
  // Preview mode
  setPreviewMode: (mode: PreviewMode) => void;

  // Viewport
  setDevicePreset: (presetId: string | null) => void;
  setCustomResolution: (width: number, height: number) => void;
  toggleOrientation: () => void;
  setScale: (scale: number) => void;
  setFitToPanel: (fit: boolean) => void;
  toggleAspectRatioLock: () => void;

  // Play mode
  play: () => void;
  pause: () => void;
  stop: () => void;
  step: () => void;
  updatePlayModeSettings: (settings: Partial<PlayModeSettings>) => void;

  // Debug
  toggleDebugOverlay: () => void;
  updateDebugOverlay: (settings: Partial<DebugOverlaySettings>) => void;
  updateDebugRender: (settings: Partial<DebugRenderSettings>) => void;
  updateStats: (stats: Partial<PerformanceStats>) => void;

  // Input
  setInputState: (state: Partial<InputState>) => void;
  toggleInputSimulation: () => void;
  toggleTouchSimulation: () => void;
  simulateKeyPress: (key: string) => void;
  simulateMouseClick: (x: number, y: number, button: number) => void;
  simulateTouch: (touches: Touch[]) => void;

  // Recording
  takeScreenshot: () => Promise<string>;
  startRecording: () => void;
  stopRecording: () => Promise<Blob>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  updateScreenshotSettings: (settings: Partial<ScreenshotSettings>) => void;
  updateRecordingSettings: (settings: Partial<RecordingSettings>) => void;

  // Reset
  reset: () => void;
}

// Default values
export const DEFAULT_VIEWPORT_SETTINGS: ViewportSettings = {
  devicePresetId: null,
  customWidth: 720,
  customHeight: 1280,
  isPortrait: false,
  scale: 1,
  fitToPanel: true,
  aspectRatioLocked: true,
  showLetterbox: true,
};

export const DEFAULT_PLAY_MODE_SETTINGS: PlayModeSettings = {
  onPlayBehavior: 'keep-layout',
  reloadScripts: true,
  resetSceneState: true,
  pauseOnStart: false,
  stepFrameOnPause: true,
  maxFps: 60,
  vsyncEnabled: true,
};

export const DEFAULT_DEBUG_OVERLAY: DebugOverlaySettings = {
  enabled: false,
  showFps: true,
  showFrameTime: true,
  showDrawCalls: true,
  showObjectCount: true,
  showMemory: false,
  showTextures: false,
  showAudio: false,
  position: 'top-left',
  opacity: 0.8,
};

export const DEFAULT_DEBUG_RENDER: DebugRenderSettings = {
  showColliders: false,
  showGrid: false,
  showBounds: false,
  showOrigins: false,
  lowLatencyMode: false,
};

export const DEFAULT_PERFORMANCE_STATS: PerformanceStats = {
  fps: 0,
  frameTime: 0,
  minFrameTime: 0,
  maxFrameTime: 0,
  avgFrameTime: 0,
  drawCalls: 0,
  triangles: 0,
  activeObjects: 0,
  visibleObjects: 0,
  memoryUsed: 0,
  memoryTotal: 0,
  texturesLoaded: 0,
  textureMemory: 0,
  audioPlaying: 0,
  audioLoaded: 0,
};

export const DEFAULT_SCREENSHOT_SETTINGS: ScreenshotSettings = {
  format: 'png',
  quality: 0.92,
  includeUI: false,
  scale: 1,
  filename: 'screenshot',
};

export const DEFAULT_RECORDING_SETTINGS: RecordingSettings = {
  format: 'mp4',
  quality: 'high',
  fps: 30,
  maxDuration: 60,
  includeAudio: true,
  filename: 'recording',
};
